import './MyWork.scss';
import { useEffect, useRef, useState } from 'react';
import { getCustomerUISearchResultsUrl } from 'Foundation/Configurations/GlobalConfiguration';
import IframeResizer from 'iframe-resizer-react';
import { useLocation } from 'react-router';
import AdvancedSearch from './AdvancedSearch';
import { SearchCustomers } from './Messages';
import { Notification } from '@lmig/lmds-react-notification';

type SearchResultsProp = {
  focusSearch: () => void;
};
export default function SearchResults({ focusSearch }: SearchResultsProp) {
  const { search } = useLocation();
  const [searchTerm, setSearchTerm] = useState<string | null>(
    new URLSearchParams(search).get('searchTerm') || new URLSearchParams(search).get('keyword'),
  );
  const [url, setUrl] = useState<string>(getCustomerUISearchResultsUrl() + '?last_name=' + searchTerm);
  const searchResultsFrame = useRef<HTMLIFrameElement>(null);

  const isClaimNumber = (test: string) => new RegExp('^\\d{9,12}').test(test);
  const isEmail = (test: string) =>
    new RegExp(
      '^$|^[_a-zA-Z0-9-]+(\\.[_a-zA-Z0-9-]+)*@d*([_a-zA-Z0-9-)]?)+(\\.[_a-zA-Z0-9-]+)*(\\.[a-zA-Z]{1,})$',
    ).test(test);
  const isPhone = (test: string) =>
    new RegExp('^$|^(\\+\\d{1,2}\\s)?\\(?\\d{3}\\)?[\\s-]?\\d{3}[\\s-]?\\d{4}$').test(test);
  const isPolicy = (test: string) =>
    new RegExp('^[eE][a-zA-Z](\\d{6})(\\d)?(\\d)?$').test(test) ||
    new RegExp('^([O|U|o|u])([a-zA-Z0-9]{1})(\\d{3,7})$').test(test) ||
    new RegExp('^([a-zA-Z0-9]{1})(\\d{3,7})$').test(test);
  const isFullName = (test: string) => new RegExp('^[a-zA-Z-]+\\s[a-zA-Z-\\s]+$').test(test);
  const isFullNameWithComma = (test: string) => new RegExp('^[a-zA-Z-]+,\\s[a-zA-Z-\\s]+$').test(test);
  const isLastName = (test: string) => new RegExp('^[a-zA-Z-]+$').test(test);

  useEffect(() => {
    if (search) {
      let term: string | null =
        new URLSearchParams(search).get('searchTerm') || new URLSearchParams(search).get('keyword');
      if (term && isPhone(term)) term = term.replaceAll(/\s|\(|\)|-/g, '');
      if (term && isPolicy(term)) term = term.toLocaleUpperCase();
      setSearchTerm(term);
    }
  }, [search]);

  useEffect(() => {
    if (searchTerm) {
      let url: string = '';
      if (isPhone(searchTerm))
        // non prod phone number 235-777-8899
        url = getCustomerUISearchResultsUrl() + `?phone_number=${searchTerm}`;
      else if (isClaimNumber(searchTerm))
        //non prod claim number 056263899
        url = getCustomerUISearchResultsUrl() + `?claim_number=${searchTerm}`;
      else if (isPolicy(searchTerm))
        //non prod policy number OK7111516
        url = getCustomerUISearchResultsUrl() + `?policy_number=${searchTerm}`;
      else if (isEmail(searchTerm)) url = getCustomerUISearchResultsUrl() + `?email_address=${searchTerm}`;
      else if (isFullName(searchTerm)) {
        let firstName: string = searchTerm.split(' ')[0]; //ignore middle names
        let lastName: string = searchTerm.split(' ').reverse()[0];
        url = getCustomerUISearchResultsUrl() + `?first_name=${firstName}&last_name=${lastName}`;
      } else if (isFullNameWithComma(searchTerm)) {
        let lastName: string = searchTerm.split(', ')[0]; //handle lastname, firstname
        let firstName: string = searchTerm.split(' ').reverse()[0];
        url = getCustomerUISearchResultsUrl() + `?first_name=${firstName}&last_name=${lastName}`;
      } else if (isLastName(searchTerm)) url = getCustomerUISearchResultsUrl() + `?last_name=${searchTerm}`;

        //append loginmode param to cui if present
        const queryString = window.location.search; 
        const urlParams = new URLSearchParams(queryString); 
        if (urlParams.has('loginmode') && url.toLowerCase().includes('loginmode') === false) 
          setUrl(url + `&loginmode=${urlParams.get('loginmode')}`);
        else setUrl(url);
    }
  }, [searchTerm]);

  const executeSearch = (message: SearchCustomers) => {
    if (message && message.data) {
      let url: string = getCustomerUISearchResultsUrl() + `?last_name=${message.data.last_name}`;
      if (message.data.zip) url += `&zip=${message.data.zip}`;
      if (message.data.state) url += `&state=${message.data.state}`;
      if (message.data.first_name) url += `&first_name=${message.data.first_name}`;
      setUrl(url);
    }
  };

  return (
    <>
      <AdvancedSearch executeSearch={executeSearch} />
      {searchTerm ? (
        <IframeResizer forwardRef={searchResultsFrame} src={url} style={{ width: '1px', minWidth: '100%' }} />
      ) : (
        <Notification
          alert="Enter a search term to view customer search results"
          highlightType="informative"
          className="mywork-notification"
          links={[
            {
              children: 'Search customers',
              onClick: () => focusSearch(),
            },
          ]}
        />
      )}
    </>
  );
}
